import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { SignupScreen } from './pages/SignupScreen';
import { LoginScreen } from './pages/LoginScreen';
import { ForgotPasswordScreen } from './pages/ForgotPasswordScreen';
import { AuthCallbackScreen } from './pages/AuthCallbackScreen';
import { SetPasswordScreen } from './pages/SetPasswordScreen';
import { ResetPasswordScreen } from './pages/ResetPasswordScreen';
import { MainAppLayout } from './MainAppLayout';
import { AttachmentUrlOverlay } from './components/chat/AttachmentUrlOverlay';
import { NotificationOnboardingScreen } from './pages/NotificationOnboardingScreen';

const ApiDocumentation = lazy(() => import('./pages/ApiDocumentation'));
const AdminCreateUserScreen = lazy(() => import('./pages/AdminCreateUserScreen'));
const AdminFakeContentScreen = lazy(() => import('./pages/AdminFakeContentScreen'));
const AdminDeleteUserScreen = lazy(() => import('./pages/AdminDeleteUserScreen'));
const AdminReschedulingSetupScreen = lazy(() => import('./pages/AdminReschedulingSetupScreen'));
const AdminPlanningSetupScreen = lazy(() => import('./pages/AdminPlanningSetupScreen'));
const AdminCrosspostSetupScreen = lazy(() => import('./pages/AdminCrosspostSetupScreen'));
const AdminNotificationsScreen = lazy(() => import('./pages/AdminNotificationsScreen'));
const AdminFeatureAccessScreen = lazy(() => import('./pages/AdminFeatureAccessScreen'));
const AdminContentManagerScreen = lazy(() => import('./pages/AdminContentManagerScreen'));
const AdminUserViewScreen = lazy(() => import('./pages/AdminUserViewScreen'));
const AdminUserContentsScreen = lazy(() => import('./pages/AdminUserContentsScreen'));
const AdminUsersScreen = lazy(() => import('./pages/AdminUsersScreen'));

const AdminMetricoolCreateBrandScreen = lazy(() => import('./pages/AdminMetricoolCreateBrandScreen'));
const AdminStatsScreen = lazy(() => import('./pages/AdminStatsScreen'));
const AdminUserStatsScreen = lazy(() => import('./pages/AdminUserStatsScreen'));
const AdminStatsDebugScreen = lazy(() => import('./pages/AdminStatsDebugScreen'));
const AdminDirectorDashboardScreen = lazy(() => import('./pages/AdminDirectorDashboardScreen'));

import { loadIntercomScript, initializeIntercom, shutdownIntercom, showIntercom, setOnHideCallback, clearOnHideCallback, type IntercomSettings } from './services/integrations/intercom';
import { Toaster } from "@/components/ui/sonner";
import { logger } from './utils/logger';
import { LoaderOne } from './components/ui/loader';
import { usePushNotifications } from './hooks/usePushNotifications';
import { useNotificationStore } from './stores/notificationStore';
import { useAuthStore } from './stores/authStore';
import { useImpersonationStore } from './stores/impersonationStore';
import { initializeOneSignal, removeOneSignalExternalUserId } from './services/oneSignal';
import { isNativePlatform, getPlatform } from './utils/platform';
import { initConsoleCapture } from './services/consoleCapture';

// Initialize console capture for bug reporting
initConsoleCapture();
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

const INTERCOM_APP_ID = 'ucer73xe';

const AuthBackground = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-black relative overflow-hidden">
    <div className="absolute inset-0">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" style={{ animationDelay: '5s' }} />
    </div>
    <div className="relative z-10">
      {children}
    </div>
  </div>
);

const RootRedirect = () => {
  const { isAuthenticated, isInitialized } = useAuthStore();
  
  if (!isInitialized) {
    return null;
  }
  
  return <Navigate to={isAuthenticated ? '/app/calendar' : '/login'} replace />;
};

const LoginRoute = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const params = new URLSearchParams(routerLocation.search);
  const initialMessage = params.get('message') || '';
  const redirectParam = params.get('redirect') || '';
  const { isAuthenticated, isInitialized } = useAuthStore();
  
  // Validate redirect to prevent open redirect attacks (must start with /app/)
  const getValidRedirect = () => {
    if (redirectParam?.startsWith('/app/')) {
      return redirectParam;
    }
    return '/app/calendar';
  };
  
  useEffect(() => {
    if (isInitialized && isAuthenticated) {
      navigate(getValidRedirect(), { replace: true });
    }
  }, [isAuthenticated, isInitialized, navigate, redirectParam]);
  
  if (isInitialized && isAuthenticated) {
    return null;
  }
  
  const handleLoginSuccess = () => {
    navigate(getValidRedirect(), { replace: true });
  };
  
  return (
    <AuthBackground>
      <LoginScreen 
        onLogin={handleLoginSuccess}
        onNavigateToSignup={() => navigate('/signup')}
        onNavigateToForgotPassword={() => navigate('/forgot-password')}
        initialSuccessMessage={initialMessage}
      />
    </AuthBackground>
  );
};

const SignupRoute = () => {
  const navigate = useNavigate();
  return (
    <AuthBackground>
      <SignupScreen 
        onSignup={() => {
          navigate('/login?message=' + encodeURIComponent('Un lien de connexion a été envoyé à votre adresse email. Vérifiez votre boîte de réception.'));
        }}
        onNavigateToLogin={() => navigate('/login')}
      />
    </AuthBackground>
  );
};

const ForgotPasswordRoute = () => {
  const navigate = useNavigate();
  return (
    <AuthBackground>
      <ForgotPasswordScreen 
        onBack={() => navigate('/login')}
      />
    </AuthBackground>
  );
};

const SetPasswordRoute = () => {
  return (
    <AuthBackground>
      <SetPasswordScreen />
    </AuthBackground>
  );
};

const ChatRoute = () => {
  const navigate = useNavigate();
  const { userEmail, userId } = useAuthStore();
  const hasHandledRef = useRef(false);

  useEffect(() => {
    if (hasHandledRef.current) return;
    
    const openIntercom = async () => {
      hasHandledRef.current = true;
      
      if (userEmail && userId) {
        try {
          if (!(globalThis as any).Intercom) {
            await loadIntercomScript(INTERCOM_APP_ID);
            const userData: IntercomSettings = {
              app_id: INTERCOM_APP_ID,
              user_id: userId,
              name: userEmail.split('@')[0],
              email: userEmail,
            };
            await initializeIntercom(userData);
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          setOnHideCallback(() => {
            navigate('/app/calendar', { replace: true });
          });
          
          await showIntercom();
        } catch (err) {
          logger.error('Error opening Intercom:', err);
        }
      }
      
      navigate('/app/calendar', { replace: true });
    };
    
    openIntercom();
    
    return () => {
      clearOnHideCallback();
    };
  }, [navigate, userEmail, userId]);

  return null;
};

export default function App() {
  // Apply dark mode on mount and initialize OneSignal
  useEffect(() => {
    document.documentElement.classList.add('dark');
    
    // Add Android class for performance optimizations
    if (getPlatform() === 'android') {
      document.documentElement.classList.add('platform-android');
    }
  }, []);

  // Configure status bar to always have black background
  useEffect(() => {
    const configureStatusBar = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          await StatusBar.setStyle({ style: Style.Dark });
          await StatusBar.setBackgroundColor({ color: '#000000' });
        } catch (error) {
          logger.debug('StatusBar configuration failed:', error);
        }
      }
    };
    configureStatusBar();
  }, []);

  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showNotificationOnboarding, setShowNotificationOnboarding] = useState(false);
  const [pendingDeepLink, setPendingDeepLink] = useState<string | null>(null);

  const { notificationOnboardingComplete, notificationOnboardingSkipped } = useNotificationStore();
  const { registerNotifications } = usePushNotifications();
  const { userId, userEmail, clearAuth, syncFromSupabase, isInitialized, isSessionReady } = useAuthStore();
  const { isImpersonating, impersonatedUser } = useImpersonationStore();

  // Centralized auth initialization - waits for Supabase session to be ready
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🔐 [App] Starting auth initialization...');
      await syncFromSupabase();
      console.log('🔐 [App] Auth initialization complete');
      setIsCheckingAuth(false);
    };

    initializeAuth();
  }, [syncFromSupabase]);

  // Handle side effects when user is authenticated (separate from initialization)
  useEffect(() => {
    if (!isSessionReady || !userId) return;

    const handleAuthenticatedUser = async () => {
      console.log('🔐 [App] Handling authenticated user:', userId);
      
      // Check if user is deactivated
      const { getUserById } = await import('./services/data/users');
      const { signOut } = await import('./services/data/auth');
      const userResult = await getUserById(userId);
      
      if (userResult.success && userResult.data?.is_active === false) {
        logger.warn('🚫 User is deactivated, signing out:', userEmail);
        await signOut();
        clearAuth();
        return;
      }

      // Initialize OneSignal
      logger.info('🔔 [App] Initializing OneSignal for user:', userId);
      await initializeOneSignal(userId);

      // Check notification onboarding
      const { notificationOnboardingComplete: isComplete, notificationOnboardingSkipped: isSkipped } = useNotificationStore.getState();
      console.log('🔔 [App] Notification onboarding check:', {
        notificationOnboardingComplete: isComplete,
        notificationOnboardingSkipped: isSkipped,
      });
      if (!isComplete && !isSkipped) {
        setShowNotificationOnboarding(true);
      }
    };

    handleAuthenticatedUser();
  }, [isSessionReady, userId, userEmail, clearAuth]);

  // Handle sign out - clean up when user becomes unauthenticated
  useEffect(() => {
    if (isSessionReady && !userId) {
      // User signed out
      logger.info('🔔 [App] User signed out, cleaning up');
      removeOneSignalExternalUserId();
      setShowNotificationOnboarding(false);
    }
  }, [isSessionReady, userId]);

  const handleNotificationOnboardingComplete = () => {
    setShowNotificationOnboarding(false);
  };

  // Reactively update showNotificationOnboarding when state changes
  useEffect(() => {
    if (notificationOnboardingComplete || notificationOnboardingSkipped) {
      setShowNotificationOnboarding(false);
    }
  }, [notificationOnboardingComplete, notificationOnboardingSkipped]);

  useEffect(() => {
    if (userId && !showNotificationOnboarding && notificationOnboardingComplete) {
      registerNotifications();
    }
  }, [userId, showNotificationOnboarding, notificationOnboardingComplete, registerNotifications]);

  // Get current location for route-based logic
  const location = useLocation();

  // Initialize Intercom when user is logged in (but not on auth-related pages)
  useEffect(() => {
    // Skip while still checking auth status to avoid stale persisted values
    if (isCheckingAuth) {
      logger.debug('📱 Skipping Intercom initialization while checking auth');
      return;
    }

    // Skip Intercom on all auth-related pages to avoid popups
    const authPages = ['/set-password', '/reset-password', '/auth/callback', '/login', '/signup', '/forgot-password'];
    const currentPath = location.pathname;
    const isAuthPage = authPages.some(page => currentPath === page || currentPath.startsWith(page));
    
    if (isAuthPage) {
      logger.debug('📱 Skipping Intercom initialization on auth page:', currentPath);
      return;
    }

    // Also skip if notification onboarding is shown on native (user hasn't completed setup)
    // On web, the onboarding screen never shows, so don't block Intercom
    if (showNotificationOnboarding && isNativePlatform()) {
      logger.debug('📱 Skipping Intercom initialization during notification onboarding');
      return;
    }
    
    if (userEmail && userId) {
      logger.debug('📱 Initializing Intercom for:', userEmail);
      logger.debug('🔑 User ID (auth):', userId);
      loadIntercomScript(INTERCOM_APP_ID).then(() => {
        try {
          const userData: IntercomSettings = {
            app_id: INTERCOM_APP_ID,
            user_id: userId,
            name: userEmail.split('@')[0],
            email: userEmail,
          };
          logger.debug('🎯 Appel initializeIntercom avec user_id:', userId);
          initializeIntercom(userData);
          logger.debug('✅ Intercom initialized successfully');
        } catch (error) {
          logger.error('❌ Error initializing Intercom:', error);
        }
      });
    }
  }, [userEmail, userId, showNotificationOnboarding, isCheckingAuth, location.pathname]);

  // Reinitialize Intercom when impersonation changes
  useEffect(() => {
    if (isCheckingAuth || !userId || !userEmail) return;

    const effectiveUserId = isImpersonating && impersonatedUser ? impersonatedUser.id : userId;
    const effectiveEmail = isImpersonating && impersonatedUser ? impersonatedUser.email : userEmail;
    const effectiveName = isImpersonating && impersonatedUser 
      ? `${impersonatedUser.first_name || ''} ${impersonatedUser.last_name || ''}`.trim() || impersonatedUser.email.split('@')[0]
      : userEmail.split('@')[0];

    logger.debug('👤 [Intercom] Reinitializing for:', { isImpersonating, effectiveUserId, effectiveEmail });

    // Shutdown existing Intercom instance and reinitialize with new user
    shutdownIntercom();
    
    loadIntercomScript(INTERCOM_APP_ID).then(() => {
      try {
        const userData: IntercomSettings = {
          app_id: INTERCOM_APP_ID,
          user_id: effectiveUserId,
          name: effectiveName,
          email: effectiveEmail,
        };
        initializeIntercom(userData);
        logger.debug('✅ Intercom reinitialized for:', effectiveEmail);
      } catch (error) {
        logger.error('❌ Error reinitializing Intercom:', error);
      }
    });
  }, [isImpersonating, impersonatedUser, userId, userEmail, isCheckingAuth]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const handleAppUrlOpen = async (event: { url: string }) => {
      logger.debug('🔗 Deep link received:', event.url);
      
      try {
        // Some iOS universal links escape query delimiters as "\?" and "\=" (or %5C? / %5C=)
        // which prevents URL parsing and drops the query parameters. Normalize them first.
        const normalizedUrlString = event.url
          .replaceAll(String.raw`\?`, '?')
          .replaceAll(String.raw`\=`, '=')
          .replaceAll('%5C?', '?')
          .replaceAll('%5c?', '?')
          .replaceAll('%5C=', '=')
          .replaceAll('%5c=', '=');

        const url = new URL(normalizedUrlString);
        const path = url.pathname || url.hostname;
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;
        
        // Preserve all query parameters (they may contain URL-encoded values like verification links)
        const searchParams = url.search;
        
        // Handle /chat deep link - show Intercom directly instead of navigating
        if (path === 'chat' || path === '/chat' || normalizedPath === '/chat') {
          // Check if auth is ready - if not, queue the deep link for later
          const { isSessionReady: sessionReady, userId: currentUserId, userEmail: currentUserEmail } = useAuthStore.getState();
          
          if (!sessionReady || !currentUserId || !currentUserEmail) {
            logger.debug('🔗 Auth not ready for chat deep link, queuing...');
            setPendingDeepLink(event.url);
            return;
          }
          
          try {            
            if (!(globalThis as any).Intercom) {
              await loadIntercomScript(INTERCOM_APP_ID);
              const userData: IntercomSettings = {
                app_id: INTERCOM_APP_ID,
                user_id: currentUserId,
                name: currentUserEmail.split('@')[0],
                email: currentUserEmail,
              };
              await initializeIntercom(userData);
              await new Promise(resolve => setTimeout(resolve, 500));
            }
            
            await showIntercom();
            return;
          } catch (err) {
            logger.error('Error opening Intercom from deep link:', err);
          }
        }
        
        let targetPath = '';
        
        if (path === 'reset-password' || path === '/reset-password' || normalizedPath === '/reset-password') {
          targetPath = '/reset-password';
        } else if (path === 'set-password' || path === '/set-password' || normalizedPath === '/set-password') {
          targetPath = '/set-password';
        } else if (path.startsWith('/app/')) {
          targetPath = path;
        } else if (path.startsWith('app/')) {
          targetPath = `/${path}`;
        } else if (path === '' || path === '/') {
          targetPath = '/';
        } else {
          targetPath = normalizedPath;
        }
        
        // Navigate with all query parameters preserved
        // Note: When creating deep links with verification URLs, ensure the verification URL is URL-encoded
        // Example: prsnl://reset-password?link=https%3A%2F%2Fexample.com%2Fverify%3Ftoken%3D...
        navigate(targetPath + searchParams);
      } catch (error) {
        logger.error('Error parsing deep link URL:', error);
      }
    };

    // Handle cold start deeplinks and in-app opens
    CapacitorApp.getLaunchUrl().then((result) => {
      if (result?.url) {
        handleAppUrlOpen({ url: result.url });
      }
    });
    CapacitorApp.addListener('appUrlOpen', handleAppUrlOpen);

    return () => {
      CapacitorApp.removeAllListeners();
    };
  }, [navigate]);

  // Process pending deep link once auth is ready
  useEffect(() => {
    if (!pendingDeepLink || !isSessionReady || !userId || !userEmail) {
      return;
    }
    
    const processPendingDeepLink = async () => {
      logger.debug('🔗 Processing pending deep link now that auth is ready:', pendingDeepLink);
      
      try {
        const url = new URL(pendingDeepLink);
        const path = url.pathname || url.hostname;
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;
        
        if (path === 'chat' || path === '/chat' || normalizedPath === '/chat') {
          if (!(globalThis as any).Intercom) {
            await loadIntercomScript(INTERCOM_APP_ID);
            const userData: IntercomSettings = {
              app_id: INTERCOM_APP_ID,
              user_id: userId,
              name: userEmail.split('@')[0],
              email: userEmail,
            };
            await initializeIntercom(userData);
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          await showIntercom();
        }
      } catch (err) {
        logger.error('Error processing pending deep link:', err);
      } finally {
        setPendingDeepLink(null);
      }
    };
    
    processPendingDeepLink();
  }, [pendingDeepLink, isSessionReady, userId, userEmail]);


  // Wait for auth initialization to complete before rendering routes
  // This ensures session is restored from Supabase before checking auth state
  if ((isCheckingAuth || !isInitialized) && !globalThis.location.pathname.startsWith('/api-docs')) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <LoaderOne />
      </div>
    );
  }

  // Don't show notification onboarding on auth pages like set-password
  const isAuthPageForOnboarding = ['/set-password', '/reset-password', '/auth/callback'].includes(location.pathname);
  const shouldShowNotificationOnboarding = showNotificationOnboarding && userId && !isAuthPageForOnboarding;

  return (
    <>
      {/* Notification onboarding - fullscreen, no app content behind - only on native */}
      {shouldShowNotificationOnboarding ? (
        <NotificationOnboardingScreen 
          userId={userId} 
          onComplete={handleNotificationOnboardingComplete} 
        />
      ) : (
        <Routes>
          {/* Public routes */}
          <Route path="/api-docs" element={
            <Suspense fallback={
              <div className="min-h-screen bg-black flex items-center justify-center">
                <LoaderOne />
              </div>
            }>
              <ApiDocumentation />
            </Suspense>
          } />
          <Route path="/admin/create-user" element={
            <Suspense fallback={
              <div className="min-h-screen bg-black flex items-center justify-center">
                <LoaderOne />
              </div>
            }>
              <AdminCreateUserScreen />
            </Suspense>
          } />
          <Route path="/admin/fake-content" element={
            <Suspense fallback={
              <div className="min-h-screen bg-black flex items-center justify-center">
                <LoaderOne />
              </div>
            }>
              <AdminFakeContentScreen />
            </Suspense>
          } />
          <Route path="/admin/delete-user" element={
            <Suspense fallback={
              <div className="min-h-screen bg-black flex items-center justify-center">
                <LoaderOne />
              </div>
            }>
              <AdminDeleteUserScreen />
            </Suspense>
          } />
          <Route path="/admin/rescheduling-setup" element={
            <Navigate to="/app/admin-rescheduling-setup" replace />
          } />
          <Route path="/admin/planning-setup" element={
            <Navigate to="/app/admin-planning-setup" replace />
          } />
          <Route path="/admin/notifications" element={
            <Suspense fallback={
              <div className="min-h-screen bg-black flex items-center justify-center">
                <LoaderOne />
              </div>
            }>
              <AdminNotificationsScreen />
            </Suspense>
          } />
          <Route path="/admin/feature-access" element={
            <Suspense fallback={
              <div className="min-h-screen bg-black flex items-center justify-center">
                <LoaderOne />
              </div>
            }>
              <AdminFeatureAccessScreen />
            </Suspense>
          } />
          <Route path="/admin/content-manager" element={
            <Suspense fallback={
              <div className="min-h-screen bg-black flex items-center justify-center">
                <LoaderOne />
              </div>
            }>
              <AdminContentManagerScreen />
            </Suspense>
          } />
          <Route path="/admin/user-view" element={
            <Suspense fallback={
              <div className="min-h-screen bg-black flex items-center justify-center">
                <LoaderOne />
              </div>
            }>
              <AdminUserViewScreen />
            </Suspense>
          } />
          <Route path="/admin/user-contents/:userId" element={
            <Navigate to="/app/admin-user-contents" replace />
          } />
          <Route path="/admin/users" element={
            <Suspense fallback={
              <div className="min-h-screen bg-black flex items-center justify-center">
                <LoaderOne />
              </div>
            }>
              <AdminUsersScreen />
            </Suspense>
          } />
          <Route path="/admin/metricool-sync" element={
            <Navigate to="/app/admin-content-manager" replace />
          } />
          <Route path="/admin/metricool-create-brand" element={
            <Suspense fallback={
              <div className="min-h-screen bg-black flex items-center justify-center">
                <LoaderOne />
              </div>
            }>
              <AdminMetricoolCreateBrandScreen />
            </Suspense>
          } />
          <Route path="/admin/stats" element={
            <Suspense fallback={
              <div className="min-h-screen bg-black flex items-center justify-center">
                <LoaderOne />
              </div>
            }>
              <AdminStatsScreen />
            </Suspense>
          } />
          <Route path="/admin/stats/:userId" element={
            <Navigate to="/app/admin-stats" replace />
          } />
          <Route path="/admin/stats-debug" element={
            <Suspense fallback={
              <div className="min-h-screen bg-black flex items-center justify-center">
                <LoaderOne />
              </div>
            }>
              <AdminStatsDebugScreen />
            </Suspense>
          } />
          <Route path="/admin/director-dashboard" element={
            <Suspense fallback={
              <div className="min-h-screen bg-black flex items-center justify-center">
                <LoaderOne />
              </div>
            }>
              <AdminDirectorDashboardScreen />
            </Suspense>
          } />
        
          {/* Auth routes */}
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/signup" element={<SignupRoute />} />
          <Route path="/forgot-password" element={<ForgotPasswordRoute />} />
          
          {/* Auth callback routes */}
          <Route path="/auth/callback" element={<AuthCallbackScreen />} />
          <Route path="/set-password" element={<SetPasswordRoute />} />
          <Route path="/reset-password" element={<ResetPasswordScreen />} />
          
          {/* Main app routes - includes all /app/* routes */}
          
          {/* Main app routes */}
          <Route path="/app/*" element={<><MainAppLayout /><AttachmentUrlOverlay /></>} />
          
          {/* Chat route - opens Intercom and redirects to /app */}
          <Route path="/chat" element={<ChatRoute />} />
          
          {/* Root redirect - check auth and redirect accordingly */}
          <Route path="/" element={<RootRedirect />} />
          
          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}

      <Toaster />
    </>
  );
}
